import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import MuiAlert from "@mui/material/Alert";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { FullPageCard } from "component/shared/full-page-card";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { YesNoRadioField } from "component/shared/yes-no-radio-field";
import { User } from "model/user";
import { saveUser } from "redux/slice/authentication";
import { Switch } from "component/shared/switch";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { styles } from "./style";
import * as yup from "yup";
import { validate } from "shared/yup";
import { DisclaimerPreviewDialog } from "./disclaimer-preview-dialog";
import {
	followUpBossDisclaimerText,
	followUpBossOptionDescription,
	followUpBossOptionLabel,
	followUpBossPreviewIntro,
	followUpBossPreviewTitle,
	privacyConsentText,
	privacyPolicyUrlRequiredError,
	reviewErrorsMessage,
	standardDisclaimerText,
	standardOptionDescription,
	standardOptionLabel,
	standardPreviewIntro,
	standardPreviewTitle,
	textingEnabledDescription,
	SMS_DISCLAIMER_TYPE_FOLLOW_UP_BOSS,
	SMS_DISCLAIMER_TYPE_STANDARD,
} from "./disclaimer-text";

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
	};
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	saveUser,
}, dispatch);

interface Props extends
	PageProps,
	Router.RouteComponentProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
	user: User;
}

interface OwnProps {
	user: User;
}
interface State {
	saving: boolean;
	saved: boolean;
	emailSignupPhoneRequired: boolean;
	showCaptchaOnForms: boolean;
	formHeaderMoreInfo: string;
	formHeaderScheduleShowing: string;
	formHeaderSaveListing: string;
	displayDisclaimer: boolean;
	disclaimerType: string;
	privacyPolicyUrl: string;
	termsOfServiceUrl: string;
	showAgentSelector: boolean;
	previewDisclaimerType?: string;
}

class Component extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			...props.user,
			saving: false,
			saved: false,
			displayDisclaimer: props.user.displayTextMessageDisclaimerOnAllContactForms,
			disclaimerType: props.user.smsDisclaimerType || SMS_DISCLAIMER_TYPE_STANDARD,
			privacyPolicyUrl: props.user.privacyPolicyUrl || "",
			termsOfServiceUrl: props.user.termsOfServiceUrl || "",
			showAgentSelector: !props.user.hideAgentSelector,
		}
	}

	private isDirty(): boolean {
		const { user } = this.props;
		const {
			emailSignupPhoneRequired,
			showCaptchaOnForms,
			formHeaderMoreInfo,
			formHeaderScheduleShowing,
			formHeaderSaveListing,
			displayDisclaimer,
			disclaimerType,
			privacyPolicyUrl,
			termsOfServiceUrl,
			showAgentSelector,
		} = this.state;
		return (
			emailSignupPhoneRequired !== user.emailSignupPhoneRequired ||
			showCaptchaOnForms !== user.showCaptchaOnForms ||
			formHeaderMoreInfo !== user.formHeaderMoreInfo ||
			formHeaderScheduleShowing !== user.formHeaderScheduleShowing ||
			formHeaderSaveListing !== user.formHeaderSaveListing ||
			displayDisclaimer !== user.displayTextMessageDisclaimerOnAllContactForms ||
			disclaimerType !== (user.smsDisclaimerType || SMS_DISCLAIMER_TYPE_STANDARD) ||
			privacyPolicyUrl !== (user.privacyPolicyUrl || "") ||
			termsOfServiceUrl !== (user.termsOfServiceUrl || "") ||
			showAgentSelector !== !user.hideAgentSelector
		);
	}

	private cancel() {
		const { user } = this.props;
		this.setState({
			...user,
			saving: false,
			saved: false,
			displayDisclaimer: user.displayTextMessageDisclaimerOnAllContactForms,
			disclaimerType: user.smsDisclaimerType || SMS_DISCLAIMER_TYPE_STANDARD,
			privacyPolicyUrl: user.privacyPolicyUrl || "",
			termsOfServiceUrl: user.termsOfServiceUrl || "",
			showAgentSelector: !user.hideAgentSelector,
			previewDisclaimerType: undefined,
		});
	}

	// The Follow Up Boss disclaimer embeds a Privacy Policy link in its single consent line, so a
	// URL is required for that variant — but only when a disclaimer is actually shown. With
	// iHomefinder texting one always is, regardless of the toggle. `validate()` is passed
	// `this.state`, so sibling names in `.when()` must match state fields exactly.
	private get validationSchema() {
		const textingEnabled = !!this.props.user.textMessagingPermitted;
		return {
			privacyPolicyUrl: yup.string()
				.url("Please enter a valid URL")
				.when(["displayDisclaimer", "disclaimerType"], {
					is: (displayDisclaimer: boolean, disclaimerType: string) =>
						(textingEnabled || displayDisclaimer) && disclaimerType === SMS_DISCLAIMER_TYPE_FOLLOW_UP_BOSS,
					then: schema => schema.required(privacyPolicyUrlRequiredError),
				}),
			termsOfServiceUrl: yup.string().url("Please enter a valid URL"),
		};
	}

	public save() {
		const { saveUser } = this.props;
		const { displayDisclaimer, disclaimerType, privacyPolicyUrl, termsOfServiceUrl, showAgentSelector } = this.state;
		const errors = validate(this.validationSchema, this.state);
		if (errors) {
			this.setState({ saved: true });
			return;
		}
		const user = {
			...this.state,
			displayTextMessageDisclaimerOnAllContactForms: displayDisclaimer,
			smsDisclaimerType: disclaimerType,
			privacyPolicyUrl: privacyPolicyUrl,
			termsOfServiceUrl: termsOfServiceUrl,
			hideAgentSelector: !showAgentSelector,
		};
		saveUser({user});
		this.setState({saving: true});
	}

	public render() {
		const {
			user,
			authLoading,
			authError,
			classes = {},
		} = this.props;
		const {
			saving,
			saved,
			emailSignupPhoneRequired,
			showCaptchaOnForms,
			formHeaderMoreInfo,
			formHeaderScheduleShowing,
			formHeaderSaveListing,
			displayDisclaimer,
			disclaimerType,
			privacyPolicyUrl,
			termsOfServiceUrl,
			showAgentSelector,
			previewDisclaimerType,
		} = this.state;
		const errors = validate(this.validationSchema, this.state);
		const followUpBossSelected = disclaimerType === SMS_DISCLAIMER_TYPE_FOLLOW_UP_BOSS;
		// True when the client uses iHomefinder texting. A disclaimer then always appears on lead
		// capture forms, so no off-switch is offered — only the type. Read-only, derived from the
		// client server-side.
		const textingEnabled = !!user.textMessagingPermitted;
		const showPrivacyPolicyUrlError = saved && !!errors?.privacyPolicyUrl;

		const radioOption = (value: string, label: string, description: string) => (
			<Mui.Box className={classes.radioOption}>
				<Mui.FormControlLabel
					value={value}
					control={<Mui.Radio />}
					className={classes.radioControl}
					label={<Mui.Typography className={classes.radioLabel}>{label}</Mui.Typography>}
				/>
				<Mui.Box className={classes.radioLabelColumn}>
					<Mui.Typography variant="body2" className={classes.radioDescription}>
						{description}
					</Mui.Typography>
					<Mui.Link
						component="button"
						type="button"
						variant="body2"
						onClick={() => this.setState({ previewDisclaimerType: value })}
					>
						View Text
					</Mui.Link>
				</Mui.Box>
			</Mui.Box>
		);

		const disclaimerTypeField = (
			<>
				<Mui.Typography className={classes.disclaimerTypeHeading}>Disclaimer Type</Mui.Typography>
				<Mui.RadioGroup
					value={disclaimerType}
					onChange={event => this.setState({ disclaimerType: event.target.value })}
				>
					{radioOption(SMS_DISCLAIMER_TYPE_STANDARD, standardOptionLabel, standardOptionDescription)}
					{radioOption(SMS_DISCLAIMER_TYPE_FOLLOW_UP_BOSS, followUpBossOptionLabel, followUpBossOptionDescription)}
				</Mui.RadioGroup>
			</>
		);

		const snackbar = (
			<Mui.Snackbar
				open={saving && !authLoading && !authError}
				message="Settings saved"
				autoHideDuration={6000}
				onClose={event => this.setState({ saving: false })}
				action={
					<Mui.IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={event => this.setState({ saving: false })}>
						<Icons.X fontSize="small" />
					</Mui.IconButton>
				}>
					<MuiAlert severity="info">
						<Mui.Typography>Settings saved</Mui.Typography>
					</MuiAlert>
			</Mui.Snackbar>
		);

		return (
			<DashboardLayout
				title="Capture Forms"
				header={
					<Mui.Typography variant="h1">Capture Forms</Mui.Typography>
				}
			>
				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
				<Mui.Typography variant="body2" className={classes.pageSubtitle}>
					Configure contact form settings across your website, including required fields, spam protection, email alert prompts, and custom form messaging.
				</Mui.Typography>
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Grid container direction="column" spacing={3}>
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionHeading}>General</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Switch
									checked={emailSignupPhoneRequired}
									onChange={value => this.setState({ emailSignupPhoneRequired: value })}
									label="Require phone number on all lead capture forms"
									description="When enabled, leads must provide a phone number to submit any lead capture form."
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Switch
									checked={showCaptchaOnForms}
									onChange={value => this.setState({ showCaptchaOnForms: value })}
									label="Require CAPTCHA on all lead capture forms"
									description="When enabled, a CAPTCHA challenge will be added to all lead capture forms, asking website visitors to complete a simple challenge to verify they are human and help reduce spam."
								/>
							</Mui.Grid>
							{user.permissions.showTeamFeatures && (
								<Mui.Grid item>
									<Switch
										checked={showAgentSelector}
										onChange={value => this.setState({ showAgentSelector: value })}
										label="Show Agent Selector on all lead capture forms"
										description="When enabled, website visitors can choose a specific agent to contact on capture forms. When disabled, leads are assigned according to your existing lead routing rules."
									/>
								</Mui.Grid>
							)}
						</Mui.Grid>
					</FullPageCard>
				</Mui.Box>
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Grid container direction="column" spacing={3}>
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionHeading}>Legal &amp; Compliance</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionSubheading} style={{ marginBottom: 24 }}>SMS &amp; Phone Disclaimer</Mui.Typography>
								{textingEnabled ? (
									// With iHomefinder texting a disclaimer always appears, so there is no
									// off-switch — the agent only chooses which version to show.
									<>
										<Mui.Typography variant="body2" className={classes.textingEnabledDescription}>
											{textingEnabledDescription}
										</Mui.Typography>
										{disclaimerTypeField}
									</>
								) : (
									<>
										<Switch
											checked={displayDisclaimer}
											onChange={value => this.setState({ displayDisclaimer: value })}
											label="Show SMS and phone number usage disclaimer"
											description="When enabled, a disclaimer about SMS and phone number usage will appear on all lead capture forms."
										/>
										{/* Indented under the toggle, per the designs. */}
										{displayDisclaimer && (
											<Mui.Box className={classes.disclaimerTypeIndent}>
												{disclaimerTypeField}
											</Mui.Box>
										)}
									</>
								)}
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Divider className={classes.sectionDivider} />
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionSubheading}>Privacy Policy &amp; Terms of Service</Mui.Typography>
								<Mui.Typography variant="body2" className={classes.sectionDescription}>
									Optionally link to your own Privacy Policy and Terms of Service pages. When provided, a consent checkbox will appear on all lead capture forms. Visitors are not required to check it to submit.
								</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Grid container alignItems="center" className={classes.fieldLabelRow}>
									<Mui.Typography className={classes.textFieldLabel} style={{ marginBottom: 0 }}>
										Privacy Policy URL{followUpBossSelected && "*"}
									</Mui.Typography>
									<Mui.Tooltip title={followUpBossSelected
										? "Link to your company's privacy policy. Required when using the Follow Up Boss–compliant disclaimer, which links to it inline."
										: "Link to your company's privacy policy. This field is optional. If left blank, no privacy policy link will appear."}>
										<span className={classes.infoIcon}>
											<Icons.Info size={16} />
										</span>
									</Mui.Tooltip>
								</Mui.Grid>
								<Mui.TextField
									fullWidth
									required={followUpBossSelected}
									variant="outlined"
									placeholder="https://example.com/privacy-policy"
									value={privacyPolicyUrl}
									onChange={event => this.setState({ privacyPolicyUrl: event.target.value })}
									error={saved && !!errors?.privacyPolicyUrl}
									helperText={saved && errors?.privacyPolicyUrl ? errors.privacyPolicyUrl : undefined}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Grid container alignItems="center" className={classes.fieldLabelRow}>
									<Mui.Typography className={classes.textFieldLabel} style={{ marginBottom: 0 }}>Terms of Service URL</Mui.Typography>
									<Mui.Tooltip title="Link to your company's terms of service. This field is optional. If left blank, no terms of service link will appear.">
										<span className={classes.infoIcon}>
											<Icons.Info size={16} />
										</span>
									</Mui.Tooltip>
								</Mui.Grid>
								<Mui.TextField
									fullWidth
									variant="outlined"
									placeholder="https://example.com/terms-of-service"
									value={termsOfServiceUrl}
									onChange={event => this.setState({ termsOfServiceUrl: event.target.value })}
									error={saved && !!errors?.termsOfServiceUrl}
									helperText={saved && errors?.termsOfServiceUrl ? errors.termsOfServiceUrl : undefined}
								/>
							</Mui.Grid>
						</Mui.Grid>
					</FullPageCard>
				</Mui.Box>
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Grid container direction="column" spacing={3}>
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionHeading}>Custom HTML Form Text</Mui.Typography>
								<Mui.Typography variant="body2" className={classes.sectionDescription}>
									Add custom HTML content to specific form types. This content will appear in addition to the existing form fields.
								</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.textFieldLabel}>"More Info Request" Form</Mui.Typography>
								<Mui.TextField
									fullWidth
									value={formHeaderMoreInfo}
									onChange={event => this.setState({ formHeaderMoreInfo: event.target.value })}
									multiline
									variant="outlined"
									minRows={4}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.textFieldLabel}>"Schedule a Showing" Form</Mui.Typography>
								<Mui.TextField
									fullWidth
									value={formHeaderScheduleShowing}
									onChange={event => this.setState({ formHeaderScheduleShowing: event.target.value })}
									multiline
									variant="outlined"
									minRows={4}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.textFieldLabel}>"Save to Favorites" Form</Mui.Typography>
								<Mui.TextField
									fullWidth
									value={formHeaderSaveListing}
									onChange={event => this.setState({ formHeaderSaveListing: event.target.value })}
									multiline
									variant="outlined"
									minRows={4}
								/>
							</Mui.Grid>
						</Mui.Grid>
					</FullPageCard>
				</Mui.Box>
				<SettingsStickyFooter
					dirty={this.isDirty()}
					saving={saving && !authLoading}
					// The footer renders "Please review errors above before saving changes." whenever
					// this is set, which is the treatment the designs call for on a blocked save.
					authError={showPrivacyPolicyUrlError ? reviewErrorsMessage : authError}
					onSave={() => this.save()}
					onCancel={() => this.cancel()}
				/>
				{snackbar}
				<DisclaimerPreviewDialog
					open={previewDisclaimerType === SMS_DISCLAIMER_TYPE_STANDARD}
					onClose={() => this.setState({ previewDisclaimerType: undefined })}
					title={standardPreviewTitle}
					intro={standardPreviewIntro}
					disclaimerText={standardDisclaimerText(user.displayName)}
					privacyConsentText={privacyConsentText(privacyPolicyUrl, termsOfServiceUrl)}
				/>
				<DisclaimerPreviewDialog
					open={previewDisclaimerType === SMS_DISCLAIMER_TYPE_FOLLOW_UP_BOSS}
					onClose={() => this.setState({ previewDisclaimerType: undefined })}
					title={followUpBossPreviewTitle}
					intro={followUpBossPreviewIntro}
					disclaimerText={followUpBossDisclaimerText(user.displayName)}
					privacyPolicyUrl={privacyPolicyUrl || undefined}
					showInlinePrivacyPolicy
				/>
			</DashboardLayout>
		);
	}

}

export const SettingsContactFormsPage = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
